import type { useRelistSettings } from "../../hooks/use-relist-settings"
import { humanizeMarketplaceId } from "../../lib/marketplace"
import { Button } from "../ui/button"
import { Modal } from "../ui/modal"

type Settings = ReturnType<typeof useRelistSettings>

export function RelistSettingsFeedback({ settings }: { settings: Settings }) {
  const { confirmation, notice } = settings
  const policy = confirmation?.policy
  const name = policy ? humanizeMarketplaceId(policy.marketplace) : ""
  const waiting = policy ? policy.pending + policy.claimed : 0
  return (
    <>
      {policy && (
        <Modal
          label={`Turn on auto relist for ${name}?`}
          onClose={settings.cancel}
          surfaceClassName="max-w-lg"
        >
          <div className="space-y-4 p-6">
            <h2 className="text-lg font-semibold">Turn on auto relist for {name}?</h2>

            <p className="text-sm">
              {`${waiting.toLocaleString()} known ${waiting === 1 ? "change is" : "changes are"} waiting. Unread source events may add more changes.`}
            </p>

            <p className="text-sm text-muted-foreground">
              Enabling allows queued changes to enter relist runs asynchronously. Deployment
              restrictions can prevent processing.
            </p>

            <p className="text-sm text-muted-foreground">
              Undo restores only the preference. Existing runs and the initial activation boundary
              remain.
            </p>

            <div className="flex flex-wrap justify-end gap-2">
              <Button onClick={settings.cancel}>Cancel</Button>

              <Button variant="primary" onClick={settings.confirm}>
                Turn on auto relist
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {notice && (
        <div
          data-relist-toast
          role="status"
          className="fixed inset-x-4 bottom-6 z-40 mx-auto flex w-fit max-w-[calc(100%-2rem)] flex-wrap items-center gap-3 rounded-xl bg-primary px-5 py-3 text-sm text-primary-foreground shadow-floating"
        >
          <span>
            {notice.policy.enabled && !notice.policy.executionEnabled
              ? `${humanizeMarketplaceId(notice.policy.marketplace)}: on, but blocked by deployment`
              : `Auto relist ${notice.policy.enabled ? "on" : "off"} for ${humanizeMarketplaceId(notice.policy.marketplace)}`}
          </span>

          {notice.canUndo && (
            <button
              type="button"
              className="min-h-11 underline underline-offset-4 focus-visible:outline focus-visible:outline-2"
              onClick={settings.undo}
            >
              Undo
            </button>
          )}

          <button
            type="button"
            aria-label="Dismiss notification"
            className="min-h-11 px-2 focus-visible:outline focus-visible:outline-2"
            onClick={settings.dismissNotice}
          >
            ×
          </button>
        </div>
      )}
    </>
  )
}
